import { lazy } from 'react'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import WebsocketSubscriptionLayout from 'src/layouts/websocket-subscription-layout'
import ResetPasswordPage from 'src/pages/auth/reset-password'
import RoleGuard from 'src/routes/role-guard'
import { withCompany } from 'src/helpers/company-slug'
import { BasicRoutesEnum as BR } from 'src/routes/routes'

const ErrorPage = lazy(() => import('src/pages/error'))
const OverviewPage = lazy(() => import('src/pages/overview'))
const GuardsPage = lazy(() => import('src/pages/guards'))
const AdminsPage = lazy(() => import('src/pages/admins'))
const ShiftsPage = lazy(() => import('src/pages/shifts'))
const LocationsPage = lazy(() => import('src/pages/locations'))
const SchedulePage = lazy(() => import('src/pages/schedule'))
const ReportsPage = lazy(() => import('src/pages/reports'))
const StatisticPage = lazy(() => import('src/pages/statistic'))
const AppPublishPage = lazy(() => import('src/pages/app-publish'))
const WhiteLabelPage = lazy(() => import('src/pages/white-label'))
const SettingsPage = lazy(() => import('src/pages/settings'))
const GlobalSearchPage = lazy(() => import('src/pages/global-search'))
const AlertsPage = lazy(() => import('src/pages/alerts'))

// Auth
const SignInPage = lazy(() => import('src/pages/auth/sign-in'))
const SignUpPage = lazy(() => import('src/pages/auth/sign-up'))
const SignUpInvitePage = lazy(() => import('src/pages/auth/sign-up-invite'))
const CreateDemoAccountPage = lazy(
  () => import('src/pages/auth/create-demo-account'),
)
const ConfirmResetPassworPage = lazy(
  () => import('src/pages/auth/confirm-reset-password-link'),
)
const LogOutPage = lazy(() => import('src/pages/auth/log-out'))

// Layouts
const UserLayout = lazy(() => import('src/layouts/user-layout'))
const ModalLayout = lazy(() => import('src/layouts/modal-layout'))
const NavBarLayout = lazy(() => import('src/layouts/navbar-layout'))

const userRouter = () => {
  return createBrowserRouter([
    {
      element: <UserLayout />,
      errorElement: <ErrorPage />,
      children: [
        { element: <LogOutPage />, path: BR.LOG_OUT },
        {
          element: <ModalLayout />,
          children: [
            { element: <SignInPage />, path: BR.SIGN_IN },
            { element: <SignUpPage />, path: BR.SIGN_UP },
            {
              element: <CreateDemoAccountPage />,
              path: BR.CREATE_DEMO_ACCOUNT,
            },
            { element: <SignUpInvitePage />, path: BR.SIGN_UP_INVITE },
            {
              element: <ConfirmResetPassworPage />,
              path: BR.CONFIRM_RESET_PASSWORD,
            },
            { element: <ResetPasswordPage />, path: BR.RESET_PASSWORD },
            {
              element: <NavBarLayout />,
              children: [
                {
                  element: <WebsocketSubscriptionLayout />,
                  children: [
                    { element: <GlobalSearchPage />, path: BR.GLOBAL_SEARCH },
                    { element: <AlertsPage />, path: BR.ALERTS },
                    { element: <OverviewPage />, path: BR.OVERVIEW },
                    { element: <GuardsPage />, path: BR.GUARDS },
                    {
                      element: (
                        <RoleGuard allowedRoles={['Owner']}>
                          <AdminsPage />
                        </RoleGuard>
                      ),
                      path: BR.ADMINS,
                    },
                    { element: <ShiftsPage />, path: BR.SHIFTS },
                    { element: <LocationsPage />, path: BR.LOCATIONS },
                    { element: <SchedulePage />, path: BR.SCHEDULE },
                    { element: <ReportsPage />, path: BR.REPORTS },
                    { element: <StatisticPage />, path: BR.STATISTIC },
                    {
                      element: (
                        <RoleGuard allowedRoles={['Owner']}>
                          <AppPublishPage />
                        </RoleGuard>
                      ),
                      path: BR.APP_PUBLISH,
                    },
                    {
                      element: (
                        <RoleGuard allowedRoles={['Owner']}>
                          <WhiteLabelPage />
                        </RoleGuard>
                      ),
                      path: BR.WHITE_LABEL,
                    },
                    { element: <SettingsPage />, path: BR.SETTINGS },
                    {
                      element: <Navigate to={withCompany(BR.OVERVIEW)} replace />,
                      path: '*',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ])
}

export const RouterProviderWithRole = () => {
  return <RouterProvider router={userRouter()} />
}
